// Волчица's summoned wolves (summonView.ts), drawn in ink: no solid body, a few hundred short brush strokes
// on the shell of a moving wolf skeleton — shell sampling makes them densest along the silhouette, so the
// outline reads while the inside stays loose. Each stroke lives under a second, flowing back along the body,
// down the legs into a smear on the ground and off the back and tail, and thins out as it dies. Only the
// head, jaw and ember eyes are solid. Strokes face the camera: flat to the eye they give the engrave pass no
// normal edges, so it outlines only the tone changes between them and the mass reads as vapour, not gravel.
// Cost per wolf: one InstancedMesh of strokes, one of team-coloured strokes, head, jaw, fangs, eyes — six
// draws, three casting shadows; stroke matrices are written straight into the instance buffer.
// Wolf faces +z, root on the ground. Tuned in the dev lab (scripts/wolvesLab.ts).
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { mk, TEAM } from './engrave.js';
import type { Team } from '../core/layout.js';

/** `moving` eases the gait in and out; `bite` 0..1 opens the jaw (an attack lunge); `toCamera` is the world
    direction to the camera (strokes turn their face to it); `gait` multiplies the stride rate (1: GAIT; a
    wolf matched to the ground plays WOLF_STRIDE against its speed, gait.ts's runRate). `t` in seconds.
    `dispose` frees the instance buffers, which engrave's release() does not know about — call it before
    release(). */
export type InkWolf = { body: THREE.Group; animate(t: number, moving: boolean, bite?: number, toCamera?: THREE.Vector3, gait?: number): void; dispose(): void; tris: number };

const STROKES = 420, TEAM_STROKES = 10;
const LEN: [number, number] = [0.17, 0.32], WID = 0.2;  // stroke length range, m; width as a share of length
const TONE: [number, number] = [0.14, 0.5];             // grey range: the lighter strokes hatch thinner, read as vapour
const LIFE: [number, number] = [0.6, 1.2];              // s
const STREAK = 1.8;                                     // how far a torn-off stroke is blown back while running, m
const RISE = 0.3;                                       // how high fraying strokes drift before they die, m

const HEAD = 0x2e2a30, BONE = 0xd6ccb4, EYE = 0xffb42a;
const TAU = Math.PI * 2;

const hash = (a: number, b: number) => { const s = Math.sin(a * 127.1 + b * 311.7) * 43758.5453; return s - Math.floor(s); };

/* ---- skeleton: a hunched, low-headed wolf as a handful of points, recomputed every frame ---- */

const SHOULDER = new THREE.Vector3(0, 0.74, 0.34), HIP = new THREE.Vector3(0, 0.68, -0.42);
const LEG_W = 0.12, UPPER = 0.38, LOWER = 0.42;
// front-left, front-right, hind-left, hind-right: a loose gallop, not a symmetric trot
const LEG_PHASE = [0, 0.5, Math.PI, Math.PI + 0.5];
const GAIT = 12.5, REACH = 0.34;  // leg phase rate, rad/s (~2 strides a second); fore-aft foot swing, m
/** How fast a planted foot slides back at GAIT (mean over the planted half-cycle), m/s at scale 1. */
export const WOLF_STRIDE = REACH * GAIT * 2 / Math.PI;

type Pose = {
  hip: THREE.Vector3; shoulder: THREE.Vector3; arch: THREE.Vector3; head: THREE.Vector3; headYaw: number; headPitch: number;
  legs: { top: THREE.Vector3; knee: THREE.Vector3; foot: THREE.Vector3 }[]; tail: THREE.Vector3[];
};

function newPose(): Pose {
  const v = () => new THREE.Vector3();
  return {
    hip: v(), shoulder: v(), arch: v(), head: v(), headYaw: 0, headPitch: 0,
    legs: LEG_PHASE.map(() => ({ top: v(), knee: v(), foot: v() })), tail: [v(), v(), v(), v()],
  };
}

/* Two-bone leg in its own (z, y) plane; `bend` picks which way the joint folds. */
function solveLeg(top: THREE.Vector3, foot: THREE.Vector3, bend: number, knee: THREE.Vector3) {
  const dz = foot.z - top.z, dy = foot.y - top.y;
  const d = Math.min(Math.hypot(dz, dy), UPPER + LOWER - 1e-3);
  const a = Math.acos(Math.max(-1, Math.min(1, (UPPER * UPPER + d * d - LOWER * LOWER) / (2 * UPPER * d))));
  const th = Math.atan2(dy, dz) + bend * a;
  knee.set(top.x, top.y + Math.sin(th) * UPPER, top.z + Math.cos(th) * UPPER);
}

function pose(p: Pose, t: number, run: number, ph: number) {
  const bob = run * 0.05 * Math.sin(ph * 2) + (1 - run) * 0.012 * Math.sin(t * 2.2);  // gallop bob / breathing
  p.shoulder.copy(SHOULDER).y += bob - run * 0.06;
  p.hip.copy(HIP).y += bob * 0.6;
  p.arch.lerpVectors(p.hip, p.shoulder, 0.45).y += 0.17 - run * 0.06;  // hunched: the spine peaks behind the shoulders
  const look = (1 - run) * (Math.sin(t * 0.6) * 0.35 + Math.sin(t * 1.7) * 0.1);
  p.headYaw = look;
  p.headPitch = run * 0.2 + 0.1 + (1 - run) * Math.sin(t * 0.9) * 0.06;
  // head low and forward of the shoulders, the stalking carriage
  p.head.set(Math.sin(look) * 0.12, p.shoulder.y + 0.02 - run * 0.08, p.shoulder.z + 0.34 + run * 0.06);
  for (const [i, leg] of p.legs.entries()) {
    const front = i < 2, side = i % 2 ? 1 : -1, legPh = ph + LEG_PHASE[i]!;
    const base = front ? p.shoulder : p.hip;
    leg.top.set(side * LEG_W, base.y - 0.06, base.z);
    leg.foot.set(side * LEG_W * 1.1, Math.max(0, Math.sin(legPh)) * 0.22 * run + 0.03, base.z + (front ? 0.04 : -0.02) - Math.cos(legPh) * REACH * run);
    solveLeg(leg.top, leg.foot, front ? 1 : -1, leg.knee);
  }
  // tail: streams back while running, hangs and sways at rest
  for (const [k, q] of p.tail.entries()) {
    const s = (k + 1) / p.tail.length;
    q.set(Math.sin(t * 2.4 - k * 0.8) * 0.1 * s * (1 - run * 0.6), p.hip.y + 0.02 - s * 0.3 * (1 - run) + run * s * 0.05, p.hip.z - 0.08 - s * 0.55);
  }
}

/* ---- the solid parts: an angular skull + snout + ears, the jaw on its own pivot so it can bite ---- */

function build(parts: [THREE.BufferGeometry, THREE.Matrix4][]) {
  return mergeGeometries(parts.map(([g, m]) => { g.applyMatrix4(m); return g.index ? g.toNonIndexed() : g; }))!;
}
const M = (x: number, y: number, z: number, rx = 0, ry = 0, rz = 0, sx = 1, sy = 1, sz = 1) =>
  new THREE.Matrix4().compose(new THREE.Vector3(x, y, z), new THREE.Quaternion().setFromEuler(new THREE.Euler(rx, ry, rz)), new THREE.Vector3(sx, sy, sz));

const headGeometry = () => build([
  [new THREE.OctahedronGeometry(0.14, 0), M(0, 0, -0.01, 0, Math.PI / 4, 0, 1, 0.75, 1.15)],
  // snout: a tapered wedge, flattened, pointing +z; the brow ridge over it puts the eyes in shadow
  [new THREE.CylinderGeometry(0.04, 0.09, 0.28, 5), M(0, -0.03, 0.2, Math.PI / 2, 0, 0, 1, 1, 0.7)],
  [new THREE.BoxGeometry(0.2, 0.035, 0.08), M(0, 0.055, 0.1, -0.25)],
  [new THREE.ConeGeometry(0.06, 0.24, 4), M(-0.08, 0.15, -0.05, -0.35, 0, 0.25)],
  [new THREE.ConeGeometry(0.06, 0.24, 4), M(0.08, 0.15, -0.05, -0.35, 0, -0.25)],
]);
const jawGeometry = () => build([[new THREE.CylinderGeometry(0.03, 0.075, 0.27, 5), M(0, 0, 0.13, Math.PI / 2, 0, 0, 1, 1, 0.5)]]);
/* Fangs: two long upper ones and a row of short ones, pale — they catch the eye inside the dark head. */
const fangGeometry = () => build(([-1, 1] as const).flatMap(side => ([[0.28, 0.1], [0.21, 0.05], [0.15, 0.05]] as const).map(([z, len]) =>
  [new THREE.ConeGeometry(0.017, len, 4), M(side * 0.042, -0.065 - len / 2, z, Math.PI)] as [THREE.BufferGeometry, THREE.Matrix4])));
/* Eyes: slanted slits, oversized so they survive the game camera (a few px each). */
const eyeGeometry = () => build(([-1, 1] as const).map(side =>
  [new THREE.SphereGeometry(0.045, 8, 6), M(side * 0.06, 0.055, 0.13, 0, 0, side * 0.35, 1.5, 0.65, 0.8)] as [THREE.BufferGeometry, THREE.Matrix4]));

/* A brush stroke: a flat lens pointed at both ends, long axis x (-0.5..0.5), face +z. */
function strokeGeometry() {
  const s = new THREE.Shape([[-0.5, 0], [-0.2, 0.42], [0.18, 0.5], [0.5, 0.06], [0.2, -0.4], [-0.15, -0.48]].map(([x, y]) => new THREE.Vector2(x!, y!)));
  return new THREE.ShapeGeometry(s);
}

const triCount = (g: THREE.BufferGeometry) => (g.index ? g.index.count : g.attributes.position!.count) / 3;

/* ---- particles ---- */

// where a particle lives; the mix below is the share of the whole cloud each slot gets
const TORSO = 0, NECK = 1, LEG = 2, TAIL = 3, FRAY = 4, MANE = 5;
const MIX: [number, number][] = [[TORSO, 0.42], [NECK, 0.08], [LEG, 0.28], [TAIL, 0.08], [FRAY, 0.08], [MANE, 0.06]];

type Cloud = {
  mesh: THREE.InstancedMesh; n: number;
  slot: Uint8Array; leg: Uint8Array; seed: Float32Array; period: Float32Array; offset: Float32Array; size: Float32Array;
  cycle: Float64Array; r: Float32Array;  // four randoms per particle, re-rolled when it is reborn
};

function makeCloud(mesh: THREE.InstancedMesh, n: number, seed0: number, teamCloud: boolean): Cloud {
  const c: Cloud = {
    mesh, n, slot: new Uint8Array(n), leg: new Uint8Array(n), seed: new Float32Array(n), period: new Float32Array(n),
    offset: new Float32Array(n), size: new Float32Array(n), cycle: new Float64Array(n).fill(-1), r: new Float32Array(n * 4),
  };
  let i = 0;
  for (const [slot, share] of teamCloud ? ([[TORSO, 0.7], [FRAY, 0.3]] as [number, number][]) : MIX) {
    const k = Math.round(share * n);
    for (let j = 0; j < k && i < n; j++, i++) c.slot[i] = slot;
  }
  for (; i < n; i++) c.slot[i] = TORSO;
  const col = new THREE.Color();
  for (let j = 0; j < n; j++) {
    const s = seed0 + j * 1.618;
    c.seed[j] = s;
    c.leg[j] = j % 4;
    c.period[j] = LIFE[0] + hash(s, 7) * (LIFE[1] - LIFE[0]);
    c.offset[j] = hash(s, 3) * 10;
    c.size[j] = (LEN[0] + hash(s, 11) * (LEN[1] - LEN[0])) * (teamCloud ? 0.7 : c.slot[j] === LEG ? 0.8 : 1);
    if (!teamCloud) {
      const g = TONE[0] + hash(s, 13) * (TONE[1] - TONE[0]);
      mesh.setColorAt(j, col.setRGB(g, g * 0.97, g * 1.04));
    }
  }
  if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  return c;
}

/** `seed`: any integer (a summon id) — wolves side by side should not move in step. */
export function buildInkWolf(team: Team, seed = 1): InkWolf {
  const body = new THREE.Group();
  const geo = strokeGeometry();
  // kept small: the hash is sin-based and loses its spread on huge arguments
  const seed0 = (Math.abs(Math.trunc(seed)) % 97 + 1) * 1000;
  // white material: each particle's grey comes from its instance colour
  const ink = mk(geo, 0xffffff, { team: 'unit', count: STROKES, double: true }) as THREE.InstancedMesh;
  const embers = mk(geo.clone(), TEAM[team], { team, count: TEAM_STROKES, double: true }) as THREE.InstancedMesh;
  // instances move every frame: the bounding sphere three.js caches from the first frame would cull them wrongly
  ink.frustumCulled = embers.frustumCulled = false;
  body.add(ink, embers);
  const clouds = [makeCloud(ink, STROKES, seed0, false), makeCloud(embers, TEAM_STROKES, seed0 + 777, true)];

  const head = new THREE.Group(), jaw = new THREE.Group();
  const hg = headGeometry(), jg = jawGeometry(), fg = fangGeometry(), eg = eyeGeometry();
  jaw.position.set(0, -0.065, 0.02);
  jaw.add(mk(jg, HEAD, { team: 'unit', cast: false }));
  head.add(mk(hg, HEAD, { team: 'unit' }), jaw, mk(fg, BONE, { team: 'unit', cast: false }), mk(eg, EYE, { fx: true, cast: false }));
  body.add(head);
  const tris = triCount(geo) * (STROKES + TEAM_STROKES) + triCount(hg) + triCount(jg) + triCount(fg) + triCount(eg);

  const p = newPose();
  const view = new THREE.Vector3(), qInv = new THREE.Quaternion(), DEFAULT_VIEW = new THREE.Vector3(0, 0.8, 0.6).normalize();
  let run = 0, phase = 0, lastT: number | null = null;

  function place(c: Cloud, t: number) {
    const m = c.mesh.instanceMatrix.array as Float32Array;
    const flow = 0.3 + run * 0.7;
    const vx = view.x, vy = view.y, vz = view.z;
    const sh = p.shoulder, hp = p.hip, ar = p.arch, hd = p.head, tl = p.tail;
    for (let i = 0; i < c.n; i++) {
      const seed = c.seed[i]!, age = (t + c.offset[i]!) / c.period[i]!, cycle = Math.floor(age), life = age - cycle;
      const r = c.r, o = i * 4;
      if (c.cycle[i] !== cycle) {
        c.cycle[i] = cycle;
        r[o] = hash(seed, cycle); r[o + 1] = hash(seed + 17, cycle); r[o + 2] = hash(seed + 41, cycle); r[o + 3] = hash(seed + 5, cycle);
      }
      const r1 = r[o]!, r2 = r[o + 1]!, r3 = r[o + 2]!, r4 = r[o + 3]!;
      const ang = r2 * TAU, ca = Math.cos(ang), sa = Math.sin(ang);
      let px: number, py: number, pz: number, dx: number, dy: number, dz: number;
      let len = c.size[i]! * (1 + run * 0.8);
      switch (c.slot[i]) {
        case NECK: case MANE: {
          // up the neck and over the back of the skull, combed backward
          const back = c.slot[i] === MANE;
          const u = back ? 1 : 0.1 + r1 * 0.8;
          px = sh.x + (hd.x - sh.x) * u + ca * (back ? 0.09 : 0.12);
          py = sh.y + (hd.y - sh.y) * u + sa * 0.1 + (back ? 0.06 : 0.04);
          pz = sh.z + (hd.z - sh.z) * u - (back ? 0.1 : 0) - life * (back ? 0.3 : 0.15) * flow * 2;
          dx = ca * 0.3; dy = -0.15; dz = -1;
          break;
        }
        case LEG: {
          // streams down the leg; past the paw it spreads into a low smear on the ground
          const leg = p.legs[c.leg[i]!]!;
          const s = r1 * 0.85 + life * 0.4;
          if (s < 1) {
            const a = s < 0.5 ? leg.top : leg.knee, b = s < 0.5 ? leg.knee : leg.foot, k = s < 0.5 ? s * 2 : s * 2 - 1;
            const w = 0.055 - s * 0.02;
            px = a.x + (b.x - a.x) * k + ca * w; py = a.y + (b.y - a.y) * k; pz = a.z + (b.z - a.z) * k + sa * w;
            dx = b.x - a.x + ca * 0.2; dy = b.y - a.y; dz = b.z - a.z + sa * 0.2;
          } else {
            const g = s - 1;
            px = leg.foot.x + ca * g * 0.45; py = 0.015 + r3 * 0.02; pz = leg.foot.z + sa * g * 0.45 - run * g * 0.8;
            dx = ca; dy = 0; dz = sa - run;
            len *= 1.2;
          }
          break;
        }
        case TAIL: {
          const k = r1 * 0.6 + life * 0.7, kk = Math.min(k, 1) * (tl.length - 1), j = Math.min(Math.floor(kk), tl.length - 2), f = kk - j;
          const a = tl[j]!, b = tl[j + 1]!, w = 0.03 + 0.07 * Math.min(k, 1);
          dx = b.x - a.x; dy = b.y - a.y; dz = b.z - a.z;
          const over = Math.max(0, k - 1) * 2;  // past the tip: carried on, frayed off the end
          px = a.x + dx * (f + over) + ca * w; py = a.y + dy * (f + over) + sa * w; pz = a.z + dz * (f + over);
          dx += ca * 0.1; dy += sa * 0.1;
          break;
        }
        default: {
          // TORSO and FRAY: on the shell around the spine (hip → arch → shoulder); torso strokes slide backward
          const fray = c.slot[i] === FRAY;
          const u = fray ? 0.1 + r1 * 0.85 : r1 * 1.1 - life * flow * 0.5;
          const uu = Math.max(0, Math.min(1, u));
          const a0 = (1 - u) * (1 - u), a1 = 2 * u * (1 - u), a2 = u * u;
          const sx = a0 * hp.x + a1 * ar.x + a2 * sh.x, sy = a0 * hp.y + a1 * ar.y + a2 * sh.y, sz = a0 * hp.z + a1 * ar.z + a2 * sh.z;
          // spine tangent (d/du), pointing tail-ward below
          const tx = 2 * (1 - u) * (ar.x - hp.x) + 2 * u * (sh.x - ar.x), ty = 2 * (1 - u) * (ar.y - hp.y) + 2 * u * (sh.y - ar.y), tz = 2 * (1 - u) * (ar.z - hp.z) + 2 * u * (sh.z - ar.z);
          const rad = 0.13 + 0.12 * uu * uu;  // narrow loins, deep chest
          if (fray) {
            // peels off the ridge of the back and drifts up and away; running, it is torn back into a streak
            px = sx + (r3 - 0.5) * 0.14 + Math.sin(t * 3 + seed) * 0.04;
            py = sy + rad * 0.8 + life * RISE * (1 - run * 0.6);
            pz = sz - life * (0.2 + run * STREAK);
            dx = 0; dy = 0.5 * (1 - run); dz = -1;
            len *= 1 + run * life * STREAK;
          } else {
            const shell = rad * (0.7 + 0.4 * r3);
            px = sx + ca * shell * 0.85;
            py = sy + sa * shell * (sa < 0 ? 1 + 0.7 * uu : 1) - 0.03;  // the chest hangs deeper than the back rises
            pz = sz + (r4 - 0.5) * 0.08;
            dx = -tx + ca * 0.35; dy = -ty + sa * 0.25; dz = -tz;
          }
        }
      }
      // a little curl so the flow never looks combed straight
      dx += Math.sin(t * 2.7 + seed) * 0.25; dy += Math.sin(t * 3.1 + seed * 1.3) * 0.2;
      // grow in fast, thin out slowly: the particle dies by shrinking
      const env = Math.min(1, life * 8) * Math.sqrt(1 - life);
      len *= env;
      let dl = Math.hypot(dx, dy, dz) || 1;
      dx /= dl; dy /= dl; dz /= dl;
      const wid = len * WID;
      // side = normalize(view × d), face = d × side: the particle lies along d with its face toward the camera
      let qx = vy * dz - vz * dy, qy = vz * dx - vx * dz, qz = vx * dy - vy * dx;
      dl = Math.hypot(qx, qy, qz) || 1;
      qx /= dl; qy /= dl; qz /= dl;
      const fx = dy * qz - dz * qy, fy = dz * qx - dx * qz, fz = dx * qy - dy * qx;
      const e = i * 16;
      m[e] = dx * len; m[e + 1] = dy * len; m[e + 2] = dz * len; m[e + 3] = 0;
      m[e + 4] = qx * wid; m[e + 5] = qy * wid; m[e + 6] = qz * wid; m[e + 7] = 0;
      m[e + 8] = fx; m[e + 9] = fy; m[e + 10] = fz; m[e + 11] = 0;
      m[e + 12] = px; m[e + 13] = py; m[e + 14] = pz; m[e + 15] = 1;
    }
    c.mesh.instanceMatrix.needsUpdate = true;
  }

  function animate(t: number, moving: boolean, bite = 0, toCamera?: THREE.Vector3, gait = 1) {
    const dt = lastT === null ? 0 : Math.max(0, Math.min(0.1, t - lastT));
    lastT = t;
    run += ((moving ? 1 : 0) - run) * (1 - Math.exp(-dt * 6));
    phase = (phase + dt * GAIT * gait) % (TAU * 1000);
    pose(p, t, run, phase);
    // strokes face the camera: its direction in the wolf's own frame
    view.copy(toCamera ?? DEFAULT_VIEW).applyQuaternion(body.getWorldQuaternion(qInv).invert());
    for (const c of clouds) place(c, t);
    head.position.copy(p.head);
    head.rotation.set(p.headPitch, p.headYaw, 0);
    // a slow snarl, a gasp while running, wide open on a bite
    jaw.rotation.x = Math.max(bite * 0.8, run * 0.25 + (1 - run) * Math.max(0, Math.sin(t * 0.8)) * 0.18);
  }
  animate(0, false);
  const dispose = () => { ink.dispose(); embers.dispose(); };
  return { body, animate, dispose, tris };
}
